/**
 * Product Card Component
 * Container component that delegates to layout-specific components
 *
 * @package
 */

import { useState, useContext, memo } from '@wordpress/element';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import { LoadingProductsContext } from '../helpers/LoadingProductsContext';
import ProductInfoModal from './ProductInfoModal';
import ProductCardHorizontal from './ProductCardHorizontal';
import ProductCardInline from './ProductCardInline';
import ProductCardGrid from './ProductCardGrid';
import ProductVariantCard from './ProductVariantCard';
import { formatPrice as formatPriceHelper, getProductLine } from '../helpers/priceHelpers';
import { useCachedProduct } from '../helpers/useApiCache';
import { handleApiError } from '../helpers/errorHandling';

const ProductCard = ( {
	productId,
	isSelected,
	onToggle,
	isLocked = false,
	isDisabled = false,
	layout = 'horizontal',
	vatMode = 'including',
	variants = [],
	activeVariantId = null,
	onSelectVariant,
	quantity = 1,
	onQuantityChange,
	workshopHourlyRate = 0,
	taxRate = 0,
} ) => {
	const [ showInfoModal, setShowInfoModal ] = useState( false );
	const loadingProductIds = useContext( LoadingProductsContext );
	const isLoading = loadingProductIds.includes( productId );

	const { data: productData, loading, error } = useCachedProduct( productId );

	if ( loading || ! productData ) {
		return (
			<div className="configurator-product-card loading" role="status" aria-live="polite">
				<span className="screen-reader-text">{ __( 'Loading product information…', 'at-vehicle-configurator' ) }</span>
				<div className="configurator-product-card-image-wrapper" aria-hidden="true">
					<span className="configurator-skeleton configurator-skeleton--image" aria-hidden="true" />
				</div>
				<div className="configurator-product-card-content">
					<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '85%', height: '18px', display: 'block' } } aria-hidden="true" />
					<div className="configurator-product-card-description" aria-hidden="true">
						<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '100%', height: '13px', display: 'block', marginTop: '8px' } } />
						<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '70%', height: '13px', display: 'block', marginTop: '6px' } } />
					</div>
					<span className="configurator-skeleton configurator-skeleton--line configurator-product-price" style={ { width: '60px', height: '16px', display: 'block', marginTop: '12px' } } aria-hidden="true" />
				</div>
			</div>
		);
	}

	if ( error ) {
		const errorMessage = handleApiError( error, 'ProductCard' );
		return (
			<div className="configurator-product-card error" role="alert">
				<span className="screen-reader-text">{ __( 'Error loading product', 'at-vehicle-configurator' ) }</span>
				{ errorMessage }
			</div>
		);
	}

	let featuredImageUrl = null;
	if ( productData._embedded?.[ 'wp:featuredmedia' ]?.[ 0 ] ) {
		featuredImageUrl = productData._embedded[ 'wp:featuredmedia' ][ 0 ].source_url;
	}

	const enrichedProductData = {
		...productData,
		featured_media_url: featuredImageUrl,
	};

	const handleInfoClick = e => {
		e.stopPropagation();
		setShowInfoModal( true );
	};

	// The card price carries the installation cost, so it matches the summary row and
	// the total rather than showing the bare product price.
	const line = getProductLine( productData, {
		vatMode,
		quantity,
		hourlyRate: workshopHourlyRate,
		taxRate,
	} );
	const formattedPrice = line.total > 0 ? formatPriceHelper( line.total ) : null;
	const allowQuantity = !! productData.allow_quantity && !! onQuantityChange;

	// Products with variants use a dedicated card: the parent is never added on
	// its own — the user picks a variant, whose product ID enters the selection.
	if ( Array.isArray( variants ) && variants.length > 0 ) {
		return (
			<>
				<ProductVariantCard
					productData={ enrichedProductData }
					variants={ variants }
					activeVariantId={ activeVariantId }
					onSelectVariant={ onSelectVariant }
					onInfoClick={ handleInfoClick }
					vatMode={ vatMode }
					isDisabled={ isLocked || isDisabled }
					allowQuantity={ allowQuantity }
					quantity={ quantity }
					onQuantityChange={ onQuantityChange }
				/>
				<ProductInfoModal
					productId={ productId }
					isOpen={ showInfoModal }
					onClose={ () => setShowInfoModal( false ) }
					isSelected={ isSelected }
				/>
			</>
		);
	}

	const commonProps = {
		productData: enrichedProductData,
		isSelected,
		isLocked,
		isDisabled,
		isLoading,
		onToggle,
		onInfoClick: handleInfoClick,
		formattedPrice,
		installationHours: line.installationHours,
		allowQuantity,
		quantity,
		onQuantityChange,
	};

	let LayoutComponent;
	if ( layout === 'inline' ) {
		LayoutComponent = ProductCardInline;
	} else if ( layout === 'grid-2' || layout === 'grid-3' ) {
		LayoutComponent = ProductCardGrid;
	} else {
		LayoutComponent = ProductCardHorizontal;
	}

	return (
		<>
			<LayoutComponent { ...commonProps } layout={ layout } />
			<ProductInfoModal
				productId={ productId }
				isOpen={ showInfoModal }
				onClose={ () => setShowInfoModal( false ) }
				isSelected={ isSelected }
			/>
		</>
	);
};

ProductCard.propTypes = {
	productId: PropTypes.number.isRequired,
	isSelected: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	isLocked: PropTypes.bool,
	isDisabled: PropTypes.bool,
	layout: PropTypes.oneOf( [ 'horizontal', 'inline', 'grid-2', 'grid-3' ] ),
	vatMode: PropTypes.oneOf( [ 'including', 'excluding' ] ),
	variants: PropTypes.array,
	activeVariantId: PropTypes.number,
	onSelectVariant: PropTypes.func,
	quantity: PropTypes.number,
	onQuantityChange: PropTypes.func,
	workshopHourlyRate: PropTypes.number,
	taxRate: PropTypes.number,
};

ProductCard.defaultProps = {
	isLocked: false,
	isDisabled: false,
	layout: 'horizontal',
	vatMode: 'including',
	variants: [],
	activeVariantId: null,
	onSelectVariant: () => {},
	quantity: 1,
	onQuantityChange: null,
	workshopHourlyRate: 0,
	taxRate: 0,
};

export default memo( ProductCard );
